import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@chakra-ui/react'
import { apiClient } from '../../api'
import { ChatSummarySchema, PaginatedChatSummariesResponse } from '../../types/chatTypes'
import { useLanguage } from '../utility/useLanguage'

export const useRenameConversation = () => {
  const queryClient = useQueryClient()
  const toast = useToast()
  const { t } = useLanguage()

  const updateTitleInCache = (chatId: string, newTitle: string) => {
    const patchPage = (page: PaginatedChatSummariesResponse): PaginatedChatSummariesResponse => ({
      ...page,
      items: page.items.map((chat: ChatSummarySchema) =>
        chat.id === chatId ? { ...chat, title: newTitle } : chat,
      ),
    })

    queryClient.setQueryData(['chatSummaries'], (oldData: any) => {
      if (!oldData?.pages) return oldData
      return { ...oldData, pages: oldData.pages.map(patchPage) }
    })

    // Patch any active search caches as well.
    queryClient.getQueriesData<any>({ queryKey: ['chatSearch'] }).forEach(([queryKey, data]) => {
      if (!data?.pages) return
      queryClient.setQueryData(queryKey, { ...data, pages: data.pages.map(patchPage) })
    })
  }

  const { mutate: renameConversation, isPending: isRenaming } = useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      apiClient.chatHistory.renameConversation(chatId, title),
    onSuccess: (data) => {
      updateTitleInCache(data.id, data.title)
    },
    onError: () => {
      toast({
        status: 'error',
        title: t('sidebar.renameConversationError', { defaultValue: 'Failed to rename conversation' }),
        duration: 4000,
        isClosable: true,
      })
    },
  })

  return { renameConversation, isRenaming }
}
